/**
 * API calls for bulk template sending functionality
 */

import { backUrl } from "../utils/backURL";
import { withAuthCredentials } from "../utils/authFetchOptions";

/**
 * Send bulk WhatsApp templates in batches to avoid timeout issues
 * @param {Object} params - Parameters for bulk template sending
 * @param {string} params.agentId - Agent ID
 * @param {Object} params.whatsappAccount - WhatsApp account configuration
 * @param {string} params.templateName - Name of the WhatsApp template
 * @param {string} params.language - Template language (e.g., "es_MX")
 * @param {Array} params.recipients - Array of recipients with phone numbers and parameters
 * @param {string} params.token - Authentication token
 * @param {number} params.batchSize - Number of recipients per batch (default: 10)
 * @param {Function} params.onProgress - Callback function to report progress (currentBatch, totalBatches, currentSent, currentFailed)
 * @returns {Promise<Object>} Response with success status and results
 */
export const sendBulkTemplates = async ({
  agentId,
  whatsappAccount,
  templateName,
  language,
  recipients,
  token,
  batchSize = 10,
  onProgress,
}) => {
  try {
    const batches = [];
    for (let i = 0; i < recipients.length; i += batchSize) {
      batches.push(recipients.slice(i, i + batchSize));
    }

    let totalSent = 0;
    let totalFailed = 0;
    const allErrors = [];
    const allSuccessfulPhoneNumbers = [];
    const allFailedPhoneNumbers = [];

    if (onProgress) {
      onProgress(0, batches.length, 0, 0);
    }

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];

      if (onProgress) {
        onProgress(batchIndex + 1, batches.length, totalSent, totalFailed);
      }

      try {
        const response = await fetch(`${backUrl}/whatsapp/bulk-template`, withAuthCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            agentId,
            whatsappAccount,
            templateName,
            language,
            recipients: batch,
          }),
        }));

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          totalSent += result.sent || 0;
          totalFailed += result.failed || 0;

          if (result.successfulPhoneNumbers && Array.isArray(result.successfulPhoneNumbers)) {
            allSuccessfulPhoneNumbers.push(...result.successfulPhoneNumbers);
          }

          if (result.failedPhoneNumbers && Array.isArray(result.failedPhoneNumbers)) {
            allFailedPhoneNumbers.push(...result.failedPhoneNumbers);
          }

          if (result.errors && result.errors.length > 0) {
            allErrors.push(...result.errors);
          }
        } else {
          totalFailed += batch.length;
          batch.forEach((recipient) => {
            allFailedPhoneNumbers.push({
              phoneNumber: recipient.phoneNumber || "N/A",
              error: `Batch ${batchIndex + 1} failed: ${result.error || "Unknown error"}`,
              timestamp: new Date().toISOString(),
            });
          });
          allErrors.push(`Batch ${batchIndex + 1} failed: ${result.error || "Unknown error"}`);
        }
      } catch (error) {
        totalFailed += batch.length;
        batch.forEach((recipient) => {
          allFailedPhoneNumbers.push({
            phoneNumber: recipient.phoneNumber || "N/A",
            error: `Batch ${batchIndex + 1} error: ${error.message}`,
            timestamp: new Date().toISOString(),
          });
        });
        allErrors.push(`Batch ${batchIndex + 1} error: ${error.message}`);
        console.error(`Error sending batch ${batchIndex + 1}:`, error);
      }

      if (onProgress) {
        onProgress(batchIndex + 1, batches.length, totalSent, totalFailed);
      }
    }

    return {
      success: true,
      sent: totalSent,
      failed: totalFailed,
      total: recipients.length,
      successfulPhoneNumbers: allSuccessfulPhoneNumbers,
      failedPhoneNumbers: allFailedPhoneNumbers,
      errors: allErrors.length > 0 ? allErrors : undefined,
    };
  } catch (error) {
    console.error("Error sending bulk templates:", error);
    return { success: false, error: error.message };
  }
};
